/* A PC link is an entry request. Only a server-approved whole build can leave it. */
(() => {
  'use strict';
  if(window.ElitePCEntry)return;
  const prefix='/apps/pc-configurator/start',keys=['case','mobo','cpu','ram','gpu','psu','cooler','wifi','sata_ssd','sata_hdd','nvme_ssd','windows_key','kettle_lead'];
  const extras=['monitors','warranty','keyboard','keyboard_mouse','mouse','headset','headphones','earphones','loudspeaker','microphone','mouse_pad','desk_pad','webcam','wrist_rest','gaming_controller'];
  const answers=new Map(),displayPlans=new Map(),pending=new Map(),workItems=new Map(),failures=new Map(),activeClicks=new WeakSet(),painted=new WeakMap(),queue=[];let active=0,background=0,automatic=0,scheduled=false,notice,displayRevision=0;
  let constraints={lockedKeys:[],budgets:[]};
  let scanState=null;
  const money=new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'});
  const selectionKey=value=>JSON.stringify([Object.entries(value.selections||{}).sort(([a],[b])=>a.localeCompare(b)),
    (value.accessories||[]).map(row=>[row.component,row.sku,String(row.variantId)]).sort((a,b)=>JSON.stringify(a).localeCompare(JSON.stringify(b)))]);
  const keyFor=body=>JSON.stringify([selectionKey(body),[...(body.lockedKeys||[])].sort(),[...(body.budgets||[])].sort(),body.allowCaseChange===true]);
  function sameDestination(left, right) {
    if (left === right) return true;
    try {
      const a = new URL(left, location.origin), b = new URL(right, location.origin);
      a.searchParams.sort(); b.searchParams.sort();
      return a.href === b.href;
    } catch { return false; }
  }
  function preparedEntry(body) {
    const api=window.ElitePCPrepared;
    if(!api?.entry || Number.isFinite(api.expiresAt)&&api.expiresAt<=Date.now())return null;
    if(!scanState)return api.entry(body);
    // All links on a card ask the same checked question. Reuse that answer
    // during this synchronous scan only; clicks, new scans and new snapshots
    // always read the current approval, including its original expiry.
    const key=keyFor(body),revision=[api.revision,api.generatedAt,api.expiresAt].join('|');
    const cached=scanState.prepared.get(key);
    if(cached?.api===api&&cached.entry===api.entry&&cached.revision===revision)return cached.value;
    const value=api.entry(body);
    scanState.prepared.set(key,{api,entry:api.entry,revision,value});
    return value;
  }
  const valid=data=>data?.success===true && data.validation?.success===true && data.validation.compatible===true
    && data.validation.checkoutReady===true && data.validation.build?.allComponentsInStock===true
    && !['incompatible','unknown'].includes(data.validation.compatibilityStatus)
    && data.validation.price?.currency==='GBP' && Number.isFinite(data.validation.price.amount) && data.validation.price.amount>0
    && /^\/products\/[a-z0-9][a-z0-9-]{0,240}\?/.test(data.url||'') && data.selection?.selections?.case && Array.isArray(data.selection.accessories);
  function wrap(href) {
    try {
      const url=new URL(href,location.origin);
      if(url.origin!==location.origin)return href;
      const direct=new URL(url);if(direct.pathname.startsWith(prefix+'/products/'))direct.pathname=direct.pathname.slice(prefix.length);
      const parts=Object.fromEntries(keys.filter(key=>direct.searchParams.has('elite_'+key)).map(key=>[key,direct.searchParams.get('elite_'+key)]));
      if(!extras.some(key=>direct.searchParams.has('elite_'+key))&&window.ElitePCPrepared?.exact(parts))return direct.pathname+direct.search+direct.hash;
      if(url.pathname.startsWith(prefix+'/products/'))return url.pathname+url.search+url.hash;
      if(!/^\/products\/[a-z0-9][a-z0-9-]*\/?$/.test(url.pathname))return href;
      return prefix+url.pathname+url.search+url.hash;
    }catch{return href;}
  }
  function productUrl(href) {
    const url=new URL(href,location.origin);if(url.pathname.startsWith(prefix+'/products/'))url.pathname=url.pathname.slice(prefix.length);return url;
  }
  function cardFor(anchor){return anchor.closest('epc-featured-spotlight,[data-elite-reference-card],[data-elite-product-card]');}
  function eligible(anchor) {
    if(!anchor?.getAttribute)return false;
    let url;try{url=new URL(anchor.getAttribute('href'),location.origin);}catch{return false;}
    return url.origin===location.origin && (url.pathname.startsWith(prefix+'/products/') ||
      /^\/products\//.test(url.pathname) && (/^gaming-pc(?:-|$)/.test(url.searchParams.get('pc')||'')
        || anchor.matches('.elite-header__build-link,.epc-mobile-configure') || cardFor(anchor)?.hasAttribute('data-home-pc-pricing')));
  }
  function parse(anchor) {
    if(!eligible(anchor))return null;
    const url=productUrl(anchor.getAttribute('href')),card=cardFor(anchor),header=anchor.matches('.elite-header__build-link,.epc-mobile-configure');
    const values=window.ElitePCPagePrice?.selection(card?.dataset.caseSku||url.searchParams.get('elite_case')||'',url.searchParams,true);
    if(values?.invalid)return null;
    const genericHeader=header && ![...url.searchParams.keys()].some(key=>/^(?:elite_|component_)/.test(key));
    const selections=genericHeader?{}:values?.selections?{...values.selections}:{};
    for(const key of keys){const list=[...url.searchParams.getAll('elite_'+key),...url.searchParams.getAll('component_'+key)];if(list.length>1)return null;if(list[0]&&!/^__no_/.test(list[0]))selections[key]=list[0];}
    const accessories=[];
    for(const component of extras)for(const sku of url.searchParams.getAll('elite_'+component)) {
      let variantId=window.ElitePCPagePrice?.extras?.[sku]?.[1];
      if(!variantId)try{variantId=JSON.parse(url.searchParams.get('entry_extras')||'[]').find(row=>row.component===component&&row.sku===sku)?.variantId;}catch{}
      if(!/^[1-9]\d*$/.test(String(variantId)))return null;
      accessories.push({component,sku,variantId:String(variantId)});
    }
    const sidebar=scanState?.sidebar??Boolean(document.querySelector('[data-elite-configurator-filter-select]'));
    const pinnedHeaderCase=header&&Boolean(selections.case)&&url.searchParams.getAll('entry_lock').includes('case');
    const lockedKeys=header?(pinnedHeaderCase?['case']:[]):[...new Set(['case',...constraints.lockedKeys,...(sidebar?[]:url.searchParams.getAll('entry_lock'))])];
    // Budget controls update before the asynchronous card/recipe refresh.
    // Read their current state on activation, not the last refresh event.
    const budgetControls=scanState?.budgetControls??document.querySelector('[data-collection-filter="budget"]');
    const selectedBudgets=scanState?.selectedBudgets||(budgetControls?[...document.querySelectorAll('[data-collection-filter="budget"]:checked')].map(input=>input.value):constraints.budgets);
    const budgets=header?[]:[...new Set([...selectedBudgets,...(sidebar||budgetControls?[]:url.searchParams.getAll('entry_budget'))])];
    const body={selections,accessories,path:url.pathname,lockedKeys,budgets,allowCaseChange:!pinnedHeaderCase&&(header||url.searchParams.get('entry_any_case')==='1')};
    return {body,key:keyFor(body),card,anchor};
  }
  function decorate(anchor) {
    const job=parse(anchor);if(!job)return null;
    const prepared=preparedEntry(job.body);
    if(prepared&&valid(prepared)&&job.body.lockedKeys.every(key=>prepared.selection.selections[key]===job.body.selections[key])) {
      const preparedUrl=new URL(prepared.url,location.origin);
      for(const key of job.body.lockedKeys)preparedUrl.searchParams.append('entry_lock',key);
      const preparedHref=preparedUrl.pathname+preparedUrl.search+preparedUrl.hash;
      if(!sameDestination(anchor.getAttribute('href'),preparedHref))anchor.setAttribute('href',preparedHref);
      anchor.removeAttribute('aria-disabled');return parse(anchor);
    }
    const url=new URL(wrap(anchor.getAttribute('href')),location.origin);
    for(const key of ['entry_lock','entry_budget','entry_extras','entry_any_case'])url.searchParams.delete(key);
    for(const key of job.body.lockedKeys)url.searchParams.append('entry_lock',key);
    for(const band of job.body.budgets)url.searchParams.append('entry_budget',band);
    if(job.body.accessories.length)url.searchParams.set('entry_extras',JSON.stringify(job.body.accessories));
    if(job.body.allowCaseChange)url.searchParams.set('entry_any_case','1');
    const href=url.pathname+url.search+url.hash;
    if(!sameDestination(anchor.getAttribute('href'),href))anchor.setAttribute('href',href);
    return job;
  }
  function peek(body) {const data=preparedEntry(body);if(data&&valid(data)&&body.lockedKeys.every(key=>data.selection.selections[key]===body.selections[key]))return {data,expiresAt:Math.min(window.ElitePCPrepared.expiresAt,Date.now()+30000)};const entry=answers.get(keyFor(body));if(entry?.expiresAt>Date.now()+500)return entry;answers.delete(keyFor(body));return null;}
  function peekSelection(selections,accessories=[]) {
    const body={selections,accessories,lockedKeys:[...new Set(['case',...constraints.lockedKeys])],budgets:constraints.budgets,allowCaseChange:false};
    return peek(body)?.data||null;
  }
  function displaySelection(selections,accessories=[]) {
    const prepared=window.ElitePCPrepared?.exact(selections,accessories);if(prepared)return {selection:{selections:prepared.selections,accessories:[]},price:prepared.price};
    const key=keyFor({selections,accessories,lockedKeys:[...new Set(['case',...constraints.lockedKeys])],budgets:constraints.budgets,allowCaseChange:false});
    const plan=displayPlans.get(key);if(plan?.expiresAt>Date.now())return plan.value;displayPlans.delete(key);return null;
  }
  function apply(job,entry) {
    const current=parse(job.anchor);if(!current||current.key!==job.key)return;
    const {data}=entry,card=job.card;
    const paintKey=JSON.stringify([job.key,data.selection,data.validation.price,
      Boolean(window.EliteCollectionPCPrices),Boolean(window.ElitePCPagePrice)]);
    // The exact approval above is still checked on every scan. Once it has
    // painted this card, its other links need no repeated whole-card rewrite.
    if(card?.dataset.eliteEntryState==='ready'&&painted.get(card)===paintKey)return;
    const links=card?[...card.querySelectorAll('a[href*="/products/"]')]:[job.anchor];
    for(const anchor of links) {
      const url=new URL(wrap(data.url),location.origin);
      for(const pin of job.body.lockedKeys)url.searchParams.append('entry_lock',pin);
      for(const band of job.body.budgets)url.searchParams.append('entry_budget',band);
      if(job.body.accessories.length)url.searchParams.set('entry_extras',JSON.stringify(data.selection.accessories));
      if(job.body.allowCaseChange)url.searchParams.set('entry_any_case','1');
      const href=url.pathname+url.search;
      if(!sameDestination(anchor.getAttribute('href'),href))anchor.setAttribute('href',href);
      anchor.removeAttribute('aria-disabled');
    }
    if(!card)return;
    // Several links belong to one card. Painting their identical checked build
    // again replaces price/spec nodes and used to trigger another whole-page scan.
    card.dataset.eliteEntryState='ready';
    const price={...data.validation.price,pennies:Math.round(data.validation.price.amount*100),formatted:money.format(data.validation.price.amount),pcPricingApplied:true};
    window.ElitePCPrivate?.rememberPrice?.(data.selection.selections,data.selection.accessories,price,entry.expiresAt);
    if(!data.selection.accessories.length && window.EliteCollectionPCPrices)window.EliteCollectionPCPrices.paint(card,{selections:data.selection.selections,price,priceState:'ready'});
    else if(!data.selection.accessories.length)window.ElitePCPagePrice?.paintCard(card);
    const badge=card.querySelector('.epc-spotlight__stock');
    if(badge){if(badge.textContent.trim()!=='In stock')badge.textContent='In stock';badge.classList.remove('epc-spotlight__stock--unavailable');badge.title='All selected parts are in stock';}
    painted.set(card,paintKey);
  }
  // Reserve a request slot for an actual click. Hovering and background scans
  // must never put a customer's navigation behind several other PCs.
  function drain(){while(active<2&&queue.length){
    const index=queue.findIndex(item=>item.intent||background===0);if(index<0)return;
    const [next]=queue.splice(index,1);next.started=true;active++;if(!next.intent)background++;
    next.run().finally(()=>{active--;if(!next.intent)background--;workItems.delete(next.key);drain();});
  }}
  function prepare(job,intent=false) {
    const cached=peek(job.body);if(cached){apply(job,cached);return Promise.resolve(cached);}
    if(pending.has(job.key)){
      const item=workItems.get(job.key);
      if(intent&&item&&!item.intent){if(item.started)background--;item.intent=true;const index=queue.indexOf(item);if(index>=0){queue.splice(index,1);queue.unshift(item);}drain();}
      return pending.get(job.key);
    }
    // A failed speculative request must not disable the next real click.
    if(!intent&&(failures.get(job.key)||0)>Date.now())return Promise.reject(new Error('Please choose another configuration or try again shortly.'));
    const run=new Promise((resolve,reject)=>{
      const work=async()=>{
        const current=parse(job.anchor);if(!current||current.key!==job.key){reject(new Error('Selection changed'));return;}
        const started=Date.now(),controller=new AbortController(),timer=setTimeout(()=>controller.abort(),35000);
        try {
          const data=await window.ElitePCPrivate.request('entry',job.body,{signal:controller.signal});
          if(!valid(data) || job.body.lockedKeys.some(key=>data.selection.selections[key]!==job.body.selections[key])
            || selectionKey({selections:{},accessories:data.selection.accessories})!==selectionKey({selections:{},accessories:job.body.accessories}))throw new Error('This configuration is not available.');
          const elapsed=Date.now()-started,transport=Math.max(0,elapsed-Math.min(elapsed,Number(data.processingMs)||0));
          const expiresAt=Date.now()+Math.max(0,Math.min(10000,Number(data.maxAgeMs)||0)-transport);
          if(expiresAt<=Date.now()+500)throw new Error('Please try this configuration again.');
          const entry={data,expiresAt};answers.set(job.key,entry);
          const resolvedKey=keyFor({...job.body,...data.selection});answers.set(resolvedKey,entry);while(answers.size>64)answers.delete(answers.keys().next().value);
          const plan={value:{selection:data.selection,price:{...data.validation.price}},expiresAt:Date.now()+300000};
          displayPlans.set(job.key,plan);displayPlans.set(resolvedKey,plan);while(displayPlans.size>64)displayPlans.delete(displayPlans.keys().next().value);
          apply(job,entry);document.dispatchEvent(new CustomEvent('elite:pc-entry:ready'));resolve(entry);
        }catch(error){failures.set(job.key,Date.now()+15000);if(job.card&&parse(job.anchor)?.key===job.key){job.card.dataset.eliteEntryState=error.code==='pc_unavailable'?'unavailable':'retry';
          if(error.code==='pc_unavailable'){displayPlans.delete(job.key);const badge=job.card.querySelector('.epc-spotlight__stock');if(badge){badge.textContent='Unavailable configuration';badge.classList.add('epc-spotlight__stock--unavailable');}job.card.querySelectorAll('a[href*="/products/"]').forEach(anchor=>anchor.setAttribute('aria-disabled','true'));}
        }reject(error);}
        finally{clearTimeout(timer);}
      };
      const item={key:job.key,intent,started:false,run:work};workItems.set(job.key,item);
      intent?queue.unshift(item):queue.push(item);drain();
    }).finally(()=>pending.delete(job.key));
    pending.set(job.key,run);return run;
  }
  function showNotice(anchor,message) {
    if(!notice){notice=document.createElement('p');notice.className='elite-pc-entry-notice';notice.setAttribute('role','status');notice.style.cssText='padding:12px 16px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#111827;font:14px/1.5 system-ui;max-width:650px';}
    notice.textContent=message;
    const original=parse(anchor),current=original&&refreshedJob(original),live=current?.anchor||anchor,card=live.isConnected?cardFor(live):null;
    (card?.querySelector('.epc-spotlight__body')||card||(live.isConnected&&live.parentElement)||document.querySelector('main')||document.body).append(notice);
  }
  function busy(anchor,value) {
    if(value){
      anchor.setAttribute('aria-busy','true');
      activeClicks.add(anchor);
      const label=[...anchor.childNodes].find(node=>node.nodeType===3&&/Configure PC|Start Configuring/i.test(node.textContent));
      if(label){anchor.dataset.eliteEntryLabel=label.textContent;label.textContent='Opening PC…';}
      anchor.querySelector('.elite-pc-entry-progress')?.remove();
      const progress=document.createElement('span');progress.className='elite-pc-entry-progress';progress.setAttribute('aria-hidden','true');anchor.append(progress);
    }else{
      activeClicks.delete(anchor);anchor.removeAttribute('aria-busy');anchor.querySelector('.elite-pc-entry-progress')?.remove();
      const label=[...anchor.childNodes].find(node=>node.nodeType===3&&node.textContent.includes('Opening PC…'));
      if(label&&anchor.dataset.eliteEntryLabel)label.textContent=anchor.dataset.eliteEntryLabel;delete anchor.dataset.eliteEntryLabel;
    }
  }
  function filterState(){return JSON.stringify([...new Set([...document.querySelectorAll('[data-collection-filter]:checked,[data-elite-configurator-filter-select],[data-elite-component-choice]:checked,[data-elite-cpu-filters] input:checked')]
    .filter(input=>input.value&&!/^__no_/.test(input.value)).map(input=>[input.dataset.param||input.dataset.collectionFilter||input.closest('[data-elite-component-list]')?.getAttribute('data-elite-component-list')||input.name,input.value].join('=')))].sort());}
  const intentKey=body=>JSON.stringify([body.path,body.allowCaseChange,[...body.budgets].sort(),
    [...body.lockedKeys].sort().map(key=>[key,body.selections[key]]),selectionKey({accessories:body.accessories})]);
  function refreshedJob(job) {
    if(job.anchor.isConnected)return decorate(job.anchor);
    for(const anchor of document.querySelectorAll('a[href*="/products/"]')){
      if(anchor.matches('[data-spotlight-details],.epc-spotlight__button--details'))continue;
      const next=parse(anchor);if(next&&intentKey(next.body)===intentKey(job.body))return decorate(anchor);
    }
    return null;
  }
  document.addEventListener('click',async event=>{
    const anchor=event.target.closest?.('a[href]');if(!eligible(anchor))return;
    // Details open locally. Their Configure PC link is checked on activation.
    if(anchor.matches('[data-spotlight-details],.epc-spotlight__button--details'))return;
    const job=decorate(anchor);if(!job){event.preventDefault();showNotice(anchor,'This configuration could not be confirmed. Please choose another PC.');return;}
    // A published exact build is already ready. Native navigation starts in this event.
    const prepared=preparedEntry(job.body);
    if(prepared&&valid(prepared)&&job.body.lockedKeys.every(key=>prepared.selection.selections[key]===job.body.selections[key])) {
      const destination=new URL(prepared.url,location.origin);
      for(const key of job.body.lockedKeys)destination.searchParams.append('entry_lock',key);
      anchor.setAttribute('href',destination.pathname+destination.search+destination.hash);return;
    }
    if(event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey||anchor.target==='_blank')return;
    event.preventDefault();if(activeClicks.has(anchor))return;
    notice?.remove();busy(anchor,true);
    const filtersAtClick=filterState(),displayAtClick=displayRevision;let next=job;
    try{
      for(let attempt=0;attempt<2;attempt++){
        let entry,error;try{entry=await prepare(next,true);}catch(value){error=value;}
        if(filterState()!==filtersAtClick)throw new Error('Selection changed');
        const current=refreshedJob(next);
        if(entry&&current&&(current.key===next.key||current.key===keyFor({...next.body,...entry.data.selection}))){location.assign(entry.data.url);return;}
        // Budget/price hydration can replace a card while its entry request is
        // running. Check its latest recipe once instead of losing the click.
        if(attempt===0&&displayRevision>displayAtClick&&current&&intentKey(current.body)===intentKey(job.body)&&current.key!==next.key){next=current;continue;}
        throw error||new Error('Selection changed');
      }
    }
    catch{showNotice(anchor,'This configuration could not be confirmed as in stock and compatible. Adjust your filters or choose another PC.');}
    finally{busy(anchor,false);schedule();}
  },true);
  for(const event of ['pointerover','focusin','touchstart'])document.addEventListener(event,input=>{
    const anchor=input.target.closest?.('a[href]');if(!eligible(anchor)||anchor.matches('[data-spotlight-details],.epc-spotlight__button--details'))return;const job=decorate(anchor);if(job)void prepare(job).catch(()=>{});
  },{passive:true});
  // Visibility arrives after the browser's normal layout. Reading each link's
  // rectangle immediately after rewriting a previous card forced synchronous
  // whole-page layouts during startup.
  const observedLinks=new WeakSet(),nearLinks=new WeakSet();
  const linkObserver=typeof IntersectionObserver==='function'?new IntersectionObserver(entries=>{
    for(const entry of entries){
      if(entry.isIntersecting){nearLinks.add(entry.target);warmVisibleLink(entry.target);}
      else nearLinks.delete(entry.target);
    }
  },{rootMargin:'200px 0px'}):null;
  function warmVisibleLink(anchor,job) {
    if(automatic>=4||navigator.connection?.saveData||anchor.closest('[hidden]')||anchor.matches('[data-spotlight-details],.epc-spotlight__button--details'))return;
    job=job||decorate(anchor);
    if(!job||pending.has(job.key)||(failures.get(job.key)||0)>Date.now())return;
    const cached=peek(job.body);if(cached){apply(job,cached);return;}
    automatic++;void prepare(job).catch(()=>{});
  }
  let scanActive=false, scanRequested=false;
  function scan() {
    scheduled=false;
    if(scanActive){scanRequested=true;return;}
    scanActive=true;
    const anchors=[...document.querySelectorAll('a[href*="/products/"]')];
    let cursor=0;
    function scanBatch(){
    const startedAt=Date.now(), initialCursor=cursor, stopAt=Math.min(cursor+8,anchors.length);
    const budgetControls=Boolean(document.querySelector('[data-collection-filter="budget"]'));
    // Share checked answers only within this short synchronous batch. Filters,
    // snapshot expiry and customer clicks can change between batches.
    scanState={prepared:new Map(),sidebar:Boolean(document.querySelector('[data-elite-configurator-filter-select]')),budgetControls,
      selectedBudgets:budgetControls?[...document.querySelectorAll('[data-collection-filter="budget"]:checked')].map(input=>input.value):constraints.budgets};
    try {
    while(cursor<stopAt && (cursor===initialCursor || Date.now()-startedAt<8)) {
      const anchor=anchors[cursor++];
      if(!anchor.isConnected)continue;
      if(anchor.getAttribute('aria-busy')==='true'&&!activeClicks.has(anchor))busy(anchor,false);
      if(!eligible(anchor))continue;const job=decorate(anchor);if(!job)continue;
      const cached=peek(job.body);if(cached){apply(job,cached);continue;}
      if(automatic>=4 || pending.has(job.key) || (failures.get(job.key)||0)>Date.now() || navigator.connection?.saveData || anchor.matches('[data-spotlight-details],.epc-spotlight__button--details'))continue;
      if(linkObserver){
        if(!observedLinks.has(anchor)){observedLinks.add(anchor);linkObserver.observe(anchor);}
        if(nearLinks.has(anchor))warmVisibleLink(anchor,job);
      }else{
        const rect=anchor.getBoundingClientRect();if(!rect.width||!rect.height||anchor.closest('[hidden]')||rect.bottom<0||rect.top>innerHeight+200)continue;
        warmVisibleLink(anchor,job);
      }
    }
    } finally { scanState=null; }
    if(cursor<anchors.length){setTimeout(scanBatch,0);return;}
    scanActive=false;
    if(scanRequested){scanRequested=false;schedule();}
    }
    scanBatch();
  }
  const schedule=()=>{if(!scheduled){scheduled=true;setTimeout(scan,0);}};
  document.addEventListener('elite:collection-configurator:change',event=>{
    constraints={lockedKeys:(event.detail?.selections||[]).filter(row=>!row.isNone&&row.value&&!/^__no_/.test(row.value)).map(row=>row.param.replace(/^(?:elite_|component_)/,'' )).filter(key=>keys.includes(key)&&key!=='case'),
      budgets:window.ElitePCBudgetBuilds?.selectedRanges?.()||[]};schedule();
  });
  document.addEventListener('elite:private-prices:updated',schedule);
  for(const event of ['elite:private-prices:updated','elite:budget-builds:updated','elite:pc-entry:ready'])document.addEventListener(event,()=>{displayRevision++;},true);
  function start(){
    if(window.ElitePCData?.pending){void window.ElitePCData.ready.then(async()=>{if(window.ElitePCData?.yield)await window.ElitePCData.yield();start();});return;}

    const style=document.createElement('style');style.textContent='a[aria-busy="true"]{position:relative;cursor:progress}.elite-pc-entry-progress{position:absolute;top:6px;right:6px;display:inline-block;width:12px;height:12px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:elite-pc-entry-spin .7s linear infinite}@keyframes elite-pc-entry-spin{to{transform:rotate(360deg)}}@media(prefers-reduced-motion:reduce){.elite-pc-entry-progress{animation:none}}';document.head.append(style);
    // Price text, gallery slides and status badges do not introduce PC links.
    // Observe navigation changes, not every text repaint on the storefront.
    new MutationObserver(records=>{
      const changed=records.some(record=>{
        if(record.type==='attributes')return record.attributeName==='href'
          ? eligible(record.target)
          : Boolean(record.target.matches?.('a[href*="/products/"],epc-featured-spotlight,[data-elite-product-card],[data-elite-reference-card],nav,[role="menu"]'));
        return [...record.addedNodes].some(node=>node.nodeType===1 &&
          (node.matches?.('a[href*="/products/"]') || node.querySelector?.('a[href*="/products/"]')));
      });
      if(changed)schedule();
    }).observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['href','hidden']});scan();
  }
  window.ElitePCEntry={wrap,productUrl,parse,prepare,peekSelection,displaySelection,valid,version:2};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
})();
